import { useEffect } from "react";

interface Category {
  categoryId: string;
  categorySlug?: string;
  subcategories: { subCategoryId: string; slug?: string }[];
}

interface FormDataWithCategory {
  categories: string;
  subCategories: string;
  categorySlug: string;
  subcategorySlug: string;
}

/**
 * Syncs category/subcategory selection from useCategories to form data.
 * Use in forms that have category/subcategory selectors.
 */
export function useCategoryFormSync<T extends FormDataWithCategory>(
  categories: Category[],
  selectedCategory: string,
  selectedSubcategory: string,
  setFormData: React.Dispatch<React.SetStateAction<T>>
): void {
  useEffect(() => {
    if (!selectedCategory) return;
    const category = categories.find((c) => c.categoryId === selectedCategory);
    if (category) {
      setFormData((prev) => ({
        ...prev,
        categories: category.categoryId,
        categorySlug: (category as { categorySlug?: string }).categorySlug ?? prev.categorySlug,
      }));
    }
  }, [selectedCategory, categories, setFormData]);

  useEffect(() => {
    if (!selectedSubcategory || !selectedCategory) return;
    const category = categories.find((c) => c.categoryId === selectedCategory);
    const subcategory = category?.subcategories.find(
      (s) => s.subCategoryId === selectedSubcategory
    );
    if (subcategory) {
      setFormData((prev) => ({
        ...prev,
        subCategories: subcategory.subCategoryId,
        subcategorySlug: (subcategory as { slug?: string }).slug ?? prev.subcategorySlug,
      }));
    }
  }, [selectedSubcategory, selectedCategory, categories, setFormData]);
}
